// G2g-1 keeper file: long-lived legacy primitives that survived the p5
// content-first migration (see .logs/b1_primitive_migration_map.md 「保留」
// rows). Implementations were moved verbatim from the p1v4 /
// p2v2 / p3v1 generation primitive files when they were
// deleted; CSS class names, DOM structure, and data attributes are unchanged.
// Business UI must keep importing these through the mobilePrimitives.tsx
// barrel aliases (Mobile*), never from this file directly.
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  Key,
  ReactNode,
} from "react";
import clsx from "clsx";

import type { MobileEmptyStateReason } from "../components/StatePanel.tsx";
import {
  createMobileReturnState,
  mobileReturnStateToQuery,
  type MobileReturnState,
} from "../util/mobilePageContext.ts";

type P1V4DecisionBarProps = HTMLAttributes<HTMLDivElement> & {
  primaryAction: ReactNode;
  secondaryActions?: ReactNode;
  status?: ReactNode;
};

export const P1V4DecisionBar = ({
  className,
  primaryAction,
  secondaryActions,
  status,
  ...divProps
}: P1V4DecisionBarProps) => (
  <div
    className={clsx("mobile-decision-bar", "p1v4-decision-bar", className)}
    data-mobile-decision-bar=""
    data-p1v4-decision-bar=""
    {...divProps}
  >
    {status ? (
      <div className="mobile-decision-bar-status p1v4-decision-bar-status">{status}</div>
    ) : null}
    <div className="mobile-decision-bar-primary p1v4-decision-bar-primary">{primaryAction}</div>
    {secondaryActions ? (
      <div className="mobile-decision-bar-secondary p1v4-decision-bar-secondary">
        {secondaryActions}
      </div>
    ) : null}
  </div>
);

type P1V4SectionProps = Omit<HTMLAttributes<HTMLElement>, "title"> & {
  children?: ReactNode;
  description?: ReactNode;
  title?: ReactNode;
};

export const P1V4Section = ({
  children,
  className,
  description,
  title,
  ...sectionProps
}: P1V4SectionProps) => (
  <section
    className={clsx("mobile-section", "p1v4-section", className)}
    data-mobile-section=""
    {...sectionProps}
  >
    {title ? <h2 className="mobile-section-title p1v4-section-title">{title}</h2> : null}
    {description ? (
      <p className="mobile-section-description p1v4-section-description">{description}</p>
    ) : null}
    {children}
  </section>
);

type P1V4FilterTriggerProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  activeCount?: number;
  label: ReactNode;
};

export const P1V4FilterTrigger = ({
  activeCount = 0,
  className,
  label,
  type = "button",
  ...buttonProps
}: P1V4FilterTriggerProps) => (
  <button
    className={clsx("mobile-filter-trigger", "p1v4-filter-trigger", className)}
    data-mobile-filter-trigger=""
    data-p1v4-filter-trigger=""
    data-p1v4-filter-count={activeCount}
    type={type}
    {...buttonProps}
  >
    <span className="mobile-filter-trigger-label p1v4-filter-trigger-label">{label}</span>
    {activeCount > 0 ? (
      <span className="mobile-filter-trigger-count p1v4-filter-trigger-count">{activeCount}</span>
    ) : null}
  </button>
);

export type P1V4ReturnStateInput = Parameters<typeof createMobileReturnState>[0];

export const createP1V4ReturnState = (input: P1V4ReturnStateInput): MobileReturnState =>
  createMobileReturnState(input);

export const createP1V4ReturnQuery = (input: P1V4ReturnStateInput) =>
  mobileReturnStateToQuery(createP1V4ReturnState(input));

export type P2V2Urgency = "critical" | "soon" | "info" | "none";

const urgencyRank: Record<P2V2Urgency, number> = {
  critical: 0,
  soon: 1,
  info: 2,
  none: 3,
};

export const getP2V2UrgencyRank = (urgency: P2V2Urgency) => urgencyRank[urgency];

type P2V2DecisionSummaryProps = Omit<HTMLAttributes<HTMLElement>, "title"> & {
  action?: ReactNode;
  conclusion: ReactNode;
  consequence?: ReactNode;
  deadline?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  urgency?: P2V2Urgency;
};

export const P2V2DecisionSummary = ({
  action,
  children,
  className,
  conclusion,
  consequence,
  deadline,
  eyebrow,
  title,
  urgency = "none",
  ...sectionProps
}: P2V2DecisionSummaryProps) => (
  <section
    className={clsx("mobile-decision-summary", "p2v2-decision-summary", className)}
    data-mobile-decision-summary=""
    data-p2v2-decision-summary=""
    data-p2v2-urgency={urgency}
    {...sectionProps}
  >
    <div className="mobile-decision-summary-body p2v2-decision-summary-body">
      {eyebrow ? (
        <div className="mobile-decision-summary-eyebrow p2v2-decision-summary-eyebrow">
          {eyebrow}
        </div>
      ) : null}
      <h2 className="mobile-decision-summary-title p2v2-decision-summary-title">{title}</h2>
      <p className="mobile-decision-summary-conclusion p2v2-decision-summary-conclusion">
        {conclusion}
      </p>
      {consequence ? (
        <div
          className="mobile-decision-summary-consequence p2v2-decision-summary-consequence"
          data-p2v2-consequence=""
        >
          {consequence}
        </div>
      ) : null}
      {deadline ? (
        <div
          className="mobile-decision-summary-deadline p2v2-decision-summary-deadline"
          data-p2v2-deadline=""
        >
          {deadline}
        </div>
      ) : null}
      {children}
    </div>
    {action ? (
      <div
        className="mobile-decision-summary-action p2v2-decision-summary-action"
        data-p2v2-primary-action=""
      >
        {action}
      </div>
    ) : null}
  </section>
);

type P2V2DecisionQueueItemProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  action?: ReactNode;
  consequence: ReactNode;
  deadline?: ReactNode;
  detail?: ReactNode;
  label?: ReactNode;
  title: ReactNode;
  urgency: P2V2Urgency;
};

export const P2V2DecisionQueueItem = ({
  action,
  className,
  consequence,
  deadline,
  detail,
  label,
  title,
  urgency,
  ...divProps
}: P2V2DecisionQueueItemProps) => (
  <div
    className={clsx("mobile-decision-queue-item", "p2v2-decision-queue-item", className)}
    data-mobile-decision-queue-item=""
    data-p2v2-decision-queue-item=""
    data-p2v2-urgency={urgency}
    {...divProps}
  >
    <div className="mobile-decision-queue-item-main p2v2-decision-queue-item-main">
      {label ? (
        <div className="mobile-decision-queue-item-label p2v2-decision-queue-item-label">
          {label}
        </div>
      ) : null}
      <div className="mobile-decision-queue-item-title p2v2-decision-queue-item-title">{title}</div>
      <div
        className="mobile-decision-queue-item-consequence p2v2-decision-queue-item-consequence"
        data-p2v2-consequence=""
      >
        {consequence}
      </div>
      {deadline ? (
        <div
          className="mobile-decision-queue-item-deadline p2v2-decision-queue-item-deadline"
          data-p2v2-deadline=""
        >
          {deadline}
        </div>
      ) : null}
      {detail ? (
        <div className="mobile-decision-queue-item-detail p2v2-decision-queue-item-detail">
          {detail}
        </div>
      ) : null}
    </div>
    {action ? (
      <div
        className="mobile-decision-queue-item-action p2v2-decision-queue-item-action"
        data-p2v2-primary-action=""
      >
        {action}
      </div>
    ) : null}
  </div>
);

export type P2V2ReturnStateInput = Parameters<typeof createMobileReturnState>[0];

export const createP2V2ReturnState = (input: P2V2ReturnStateInput): MobileReturnState =>
  createMobileReturnState(input);

export const createP2V2ReturnQuery = (input: P2V2ReturnStateInput) =>
  mobileReturnStateToQuery(createP2V2ReturnState(input));

type P3V1ArchiveSeasonNavProps = HTMLAttributes<HTMLElement> & {
  currentLabel: ReactNode;
  currentValue: ReactNode;
  nextHref: string;
  nextLabel: ReactNode;
  previousHref: string;
  previousLabel: ReactNode;
  rootHref?: string;
  rootLabel?: ReactNode;
};

export const P3V1ArchiveSeasonNav = ({
  className,
  currentLabel,
  currentValue,
  nextHref,
  nextLabel,
  previousHref,
  previousLabel,
  rootHref,
  rootLabel,
  ...navProps
}: P3V1ArchiveSeasonNavProps) => (
  <nav
    className={clsx("mobile-archive-season-nav", "p3v1-archive-season-nav", className)}
    data-mobile-archive-season-nav=""
    data-p3v1-archive-season-nav=""
    {...navProps}
  >
    <a
      className="btn btn-outline-secondary mobile-archive-season-nav-link p3v1-archive-season-nav-link"
      data-mobile-archive-season-prev=""
      href={previousHref}
    >
      {previousLabel}
    </a>
    <div
      className="mobile-archive-season-nav-current p3v1-archive-season-nav-current"
      data-mobile-archive-season-current=""
    >
      <span className="mobile-archive-season-nav-label p3v1-archive-season-nav-label">
        {currentLabel}
      </span>
      <strong>{currentValue}</strong>
    </div>
    <a
      className="btn btn-outline-secondary mobile-archive-season-nav-link p3v1-archive-season-nav-link"
      data-mobile-archive-season-next=""
      href={nextHref}
    >
      {nextLabel}
    </a>
    {rootHref && rootLabel ? (
      <a
        className="btn btn-link mobile-archive-season-nav-root p3v1-archive-season-nav-root"
        data-mobile-archive-season-root=""
        href={rootHref}
      >
        {rootLabel}
      </a>
    ) : null}
  </nav>
);

export type P3V1TimelineItem = {
  detail?: ReactNode;
  key?: Key;
  label: ReactNode;
  meta?: ReactNode;
  title: ReactNode;
};

type P3V1TimelineProps = Omit<HTMLAttributes<HTMLElement>, "title"> & {
  emptyLabel?: ReactNode;
  items: readonly P3V1TimelineItem[];
  title?: ReactNode;
};

export const P3V1Timeline = ({
  className,
  emptyLabel,
  items,
  title,
  ...sectionProps
}: P3V1TimelineProps) => (
  <section
    className={clsx("mobile-timeline", "p3v1-timeline", className)}
    data-mobile-timeline=""
    data-p3v1-timeline=""
    {...sectionProps}
  >
    {title ? <h2 className="mobile-timeline-title p3v1-timeline-title">{title}</h2> : null}
    {items.length > 0 ? (
      <ol className="mobile-timeline-list p3v1-timeline-list">
        {items.map((item, index) => (
          <li className="mobile-timeline-item p3v1-timeline-item" key={item.key ?? index}>
            <div className="mobile-timeline-item-label p3v1-timeline-item-label">{item.label}</div>
            <div className="mobile-timeline-item-main p3v1-timeline-item-main">
              <div className="mobile-timeline-item-title p3v1-timeline-item-title">
                {item.title}
              </div>
              {item.meta ? (
                <div className="mobile-timeline-item-meta p3v1-timeline-item-meta">{item.meta}</div>
              ) : null}
              {item.detail ? (
                <div className="mobile-timeline-item-detail p3v1-timeline-item-detail">
                  {item.detail}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    ) : (
      <div className="mobile-timeline-empty p3v1-timeline-empty" data-p3v1-empty-state="">
        {emptyLabel}
      </div>
    )}
  </section>
);

export const getP3V1VisibleRankingRows = <T,>(rows: readonly T[], limit = 8) =>
  rows.slice(0, Math.max(0, limit));

export type P3V1ReturnStateInput = Parameters<typeof createMobileReturnState>[0];

export const createP3V1ReturnState = (input: P3V1ReturnStateInput): MobileReturnState =>
  createMobileReturnState(input);

export const createP3V1ReturnQuery = (input: P3V1ReturnStateInput) =>
  mobileReturnStateToQuery(createP3V1ReturnState(input));

// G2g-1: moved verbatim from p1SecondAuditPrimitives.tsx when the P1/P2
// second-audit generation primitive files retired. CSS class names, DOM
// structure, and data attributes are unchanged; business UI keeps importing it
// through the mobilePrimitives.tsx MobileActionableEmptyState barrel alias.
// G4-1: `action` is optional. A "tool" container (bottom sheet) whose only exit
// is its own header X must NOT render a second close-semantic button in an empty
// state (that duplicate is exactly UX review 意见1). When no action is supplied
// the empty state is a pure title + reason + impact statement and the action
// slot is omitted (data-mobile-empty-state-action="none").
type P1SecondAuditActionableEmptyStateProps = HTMLAttributes<HTMLDivElement> & {
  action?: ReactNode;
  impact: ReactNode;
  reason: MobileEmptyStateReason;
  reasonText: ReactNode;
  title: ReactNode;
};

export const P1SecondAuditActionableEmptyState = ({
  action,
  className,
  impact,
  reason,
  reasonText,
  title,
  ...divProps
}: P1SecondAuditActionableEmptyStateProps) => (
  <div
    className={clsx("mobile-actionable-empty-state", "p1-s01-actionable-empty-state", className)}
    data-mobile-empty-state=""
    data-mobile-empty-state-action={action !== undefined ? "available" : "none"}
    data-mobile-empty-state-reason={reason}
    data-p1-s01-empty-state=""
    role="status"
    {...divProps}
  >
    <h3 className="mobile-actionable-empty-state-title p1-s01-actionable-empty-state-title">
      {title}
    </h3>
    <p
      className="mobile-actionable-empty-state-reason p1-s01-actionable-empty-state-reason"
      data-mobile-empty-state-copy=""
      data-p1-s01-empty-state-reason=""
    >
      {reasonText}
    </p>
    <p
      className="mobile-actionable-empty-state-impact p1-s01-actionable-empty-state-impact"
      data-mobile-empty-state-copy=""
      data-p1-s01-empty-state-impact=""
    >
      {impact}
    </p>
    {action !== undefined ? (
      <div
        className="mobile-actionable-empty-state-action p1-s01-actionable-empty-state-action"
        data-p1-s01-empty-state-action=""
      >
        {action}
      </div>
    ) : null}
  </div>
);
